import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI, bookingsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarPicker } from '@/components/ui/calendar';
import { toast } from 'sonner';
import { UserPlus, CheckCircle, Loader2, CalendarDays, Search, FileCheck, XCircle, Camera, Users, Upload } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { statusColors } from './AdminShared';
import { BookingDetailModal } from './BookingDetailModal';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export const BookingsTab = ({ bookings, activities, sites, clients, loadData, isFranchise, user, t, lang, bookingTrigger }) => {
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [bookingForm, setBookingForm] = useState({ client_id: '', client_name: '', client_email: '', client_phone: '', activity_id: '', site_id: '', date: '', time_slot: '', duration_minutes: 30, quantity: 1, payment_status: 'confirmed', notes: '', origin_url: '' });
  const [bookingSlots, setBookingSlots] = useState([]);
  const [bookingSlotsLoading, setBookingSlotsLoading] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [clientMode, setClientMode] = useState('existing');
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const [searchFilter, setSearchFilter] = useState('');
  const [uploadingFor, setUploadingFor] = useState(null);

  const filteredActivitiesForBooking = activities.filter(a => !bookingForm.site_id || a.site_id === bookingForm.site_id);
  const selectedActivityForBooking = activities.find(a => a.id === bookingForm.activity_id);
  const filteredClients = clients.filter(c => !clientSearch || c.name.toLowerCase().includes(clientSearch.toLowerCase()) || c.email.toLowerCase().includes(clientSearch.toLowerCase()));

  // Filter bookings by search
  const displayedBookings = bookings.filter(b => {
    if (!searchFilter) return true;
    const q = searchFilter.toLowerCase();
    return (b.client_name || b.user_name || '').toLowerCase().includes(q) ||
      (b.client_email || b.user_email || '').toLowerCase().includes(q) ||
      (b.activity_name || '').toLowerCase().includes(q) ||
      (b.date || '').includes(q);
  });

  useEffect(() => {
    if (bookingTrigger) {
      const dateStr = bookingTrigger.date ? format(bookingTrigger.date, 'yyyy-MM-dd') : '';
      setBookingForm({ client_id: '', client_name: '', client_email: '', client_phone: '', activity_id: '', site_id: isFranchise ? user.site_id : (sites[0]?.id || ''), date: dateStr, time_slot: bookingTrigger.time_slot || '', duration_minutes: 30, quantity: 1, payment_status: 'confirmed', notes: '', origin_url: window.location.origin });
      setClientMode('existing'); setClientSearch(''); setBookingSlots([]);
      setShowBookingForm(true);
    }
  }, [bookingTrigger]);

  const fetchBookingSlots = useCallback(async () => {
    if (!bookingForm.activity_id || !bookingForm.date || !bookingForm.duration_minutes) { setBookingSlots([]); return; }
    setBookingSlotsLoading(true);
    try {
      const res = await bookingsAPI.getSlots(bookingForm.activity_id, bookingForm.date, bookingForm.duration_minutes);
      setBookingSlots(res.data.slots || []);
    } catch { setBookingSlots([]); }
    setBookingSlotsLoading(false);
  }, [bookingForm.activity_id, bookingForm.date, bookingForm.duration_minutes]);

  useEffect(() => { if (showBookingForm) fetchBookingSlots(); }, [fetchBookingSlots, showBookingForm]);

  const openNewBooking = () => {
    setBookingForm({ client_id: '', client_name: '', client_email: '', client_phone: '', activity_id: '', site_id: isFranchise ? user.site_id : (sites[0]?.id || ''), date: '', time_slot: '', duration_minutes: 30, quantity: 1, payment_status: 'confirmed', notes: '', origin_url: window.location.origin });
    setClientMode('existing'); setClientSearch(''); setBookingSlots([]);
    setShowBookingForm(true);
  };

  const selectClientForBooking = (c) => {
    setBookingForm(p => ({ ...p, client_id: c.id, client_name: c.name, client_email: c.email, client_phone: c.phone || '' }));
    setClientSearch(c.name);
  };

  const confirmOnsitePayment = async (bookingId, data) => {
    try { await adminAPI.confirmOnsitePayment(bookingId, data); toast.success(data?.payment_detail_method ? `Paiement confirmé (${data.payment_detail_method})` : 'Paiement confirmé'); loadData(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleQuickPhotoUpload = async (bookingId, e) => {
    e.stopPropagation();
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploadingFor(bookingId);
    let count = 0;
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        await adminAPI.uploadBookingPhoto(bookingId, formData);
        count++;
      } catch { toast.error(`Erreur: ${file.name}`); }
    }
    if (count > 0) {
      toast.success(`${count} photo(s) uploadee(s) avec filigrane`);
      loadData();
    }
    setUploadingFor(null);
    e.target.value = '';
  };

  const saveBooking = async () => {
    if (clientMode === 'existing' && !bookingForm.client_id) { toast.error('Selectionnez un client'); return; }
    if (clientMode === 'new' && (!bookingForm.client_name || !bookingForm.client_email)) { toast.error('Nom et email requis'); return; }
    if (!bookingForm.activity_id || !bookingForm.date || !bookingForm.time_slot) { toast.error('Activite, date et creneau requis'); return; }
    try {
      const payload = { ...bookingForm };
      if (clientMode === 'existing') { payload.client_name = null; payload.client_email = null; }
      else { payload.client_id = null; }
      const res = await adminAPI.createBooking(payload);
      toast.success(`Reservation creee - ${res.data.price}EUR`);
      setShowBookingForm(false); loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  return (
    <>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
        <h3 className="font-heading text-lg font-bold text-white">{t('admin.bookings')} ({bookings.length})</h3>
        <div className="flex gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:flex-none">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              value={searchFilter}
              onChange={e => setSearchFilter(e.target.value)}
              placeholder="Rechercher client, activite, date..."
              className="pl-9 bg-[#0A0A0A] border-[#2A2A2A] text-white w-full sm:w-64"
              data-testid="bookings-search-input"
            />
          </div>
          <Button onClick={openNewBooking} className="bg-[#00FF00] text-black hover:bg-[#00CC00] font-bold" data-testid="create-booking-btn">
            <UserPlus className="w-4 h-4 mr-2" /> {t('admin.new_booking')}
          </Button>
        </div>
      </div>

      <div className="bg-[#1A1A1A] rounded-2xl border border-[#2A2A2A] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-bookings-table">
            <thead className="bg-[#111] border-b border-[#2A2A2A]">
              <tr>
                <th className="text-left p-2 sm:p-3 font-semibold text-slate-400 text-[10px] sm:text-xs whitespace-nowrap">Client</th>
                <th className="text-left p-2 sm:p-3 font-semibold text-slate-400 text-[10px] sm:text-xs whitespace-nowrap">Activite</th>
                <th className="text-center p-2 sm:p-3 font-semibold text-slate-400 text-[10px] sm:text-xs whitespace-nowrap">Pers.</th>
                <th className="text-left p-2 sm:p-3 font-semibold text-slate-400 text-[10px] sm:text-xs whitespace-nowrap">Date</th>
                <th className="text-left p-2 sm:p-3 font-semibold text-slate-400 text-[10px] sm:text-xs whitespace-nowrap hidden sm:table-cell">Creneau</th>
                <th className="text-left p-2 sm:p-3 font-semibold text-slate-400 text-[10px] sm:text-xs whitespace-nowrap hidden md:table-cell">Prix</th>
                <th className="text-left p-2 sm:p-3 font-semibold text-slate-400 text-[10px] sm:text-xs whitespace-nowrap hidden md:table-cell">Règlement</th>
                <th className="text-left p-2 sm:p-3 font-semibold text-slate-400 text-[10px] sm:text-xs whitespace-nowrap">Statut</th>
                <th className="text-left p-2 sm:p-3 font-semibold text-slate-400 text-[10px] sm:text-xs whitespace-nowrap hidden lg:table-cell">Contrat</th>
                <th className="text-left p-2 sm:p-3 font-semibold text-slate-400 text-[10px] sm:text-xs whitespace-nowrap hidden lg:table-cell">Photos</th>
                <th className="text-left p-2 sm:p-3 font-semibold text-slate-400 text-[10px] sm:text-xs whitespace-nowrap hidden sm:table-cell">Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayedBookings.map(b => {
                const contractOk = b.contract_signed;
                const participantsOk = b.participants_registered;
                const photoCount = b.photo_count || 0;
                return (
                  <tr
                    key={b.id}
                    className="border-b border-[#1A1A1A] hover:bg-[#222] cursor-pointer transition-colors"
                    onClick={() => setSelectedBookingId(b.id)}
                    data-testid={`booking-${b.id}`}
                  >
                    {/* Client */}
                    <td className="p-2 sm:p-3">
                      <p className="font-medium text-white text-xs sm:text-sm leading-tight truncate max-w-[120px] sm:max-w-none">{b.client_name || b.user_name}</p>
                      <p className="text-[10px] text-slate-600 mt-0.5 truncate max-w-[120px] sm:max-w-none">{b.client_email || b.user_email}</p>
                      {b.user_phone && <p className="text-[10px] text-slate-600 hidden sm:block">{b.user_phone}</p>}
                    </td>
                    {/* Activity */}
                    <td className="p-2 sm:p-3">
                      <p className="text-slate-300 text-xs sm:text-sm truncate max-w-[80px] sm:max-w-none">{b.activity_name}</p>
                      <p className="text-[10px] text-slate-600">{b.duration_minutes}min</p>
                    </td>
                    {/* Participants */}
                    <td className="p-2 sm:p-3 text-center">
                      <span className="inline-flex items-center justify-center bg-[#00FF00]/10 text-[#00FF00] font-bold text-xs rounded-full w-7 h-7" data-testid={`qty-${b.id}`}>{b.quantity || 1}</span>
                    </td>
                    {/* Date */}
                    <td className="p-2 sm:p-3 text-slate-300 text-xs sm:text-sm whitespace-nowrap">{b.date}</td>
                    {/* Time */}
                    <td className="p-2 sm:p-3 text-slate-300 text-xs sm:text-sm hidden sm:table-cell">{b.time_slot}</td>
                    {/* Price */}
                    <td className="p-2 sm:p-3 font-semibold text-[#00FF00] whitespace-nowrap text-xs sm:text-sm hidden md:table-cell">{b.price}EUR</td>
                    {/* Payment + Status combined */}
                    <td className="p-2 sm:p-3 hidden md:table-cell">
                      {b.status === 'confirmed' ? (
                        <div className="flex flex-col gap-1">
                          <Badge className="bg-[#00FF00]/15 text-[#00FF00] border-0 text-[10px] font-bold w-fit" data-testid={`paid-badge-${b.id}`}>
                            Payé
                          </Badge>
                          {b.payment_detail_method && (
                            <span className="text-[10px] text-slate-400 font-medium" data-testid={`pm-detail-${b.id}`}>
                              {b.payment_detail_method === 'autres' ? (b.payment_detail_other || 'Autres')
                                : b.payment_detail_method === 'cb' ? 'CB'
                                : b.payment_detail_method === 'especes' ? 'Espèces'
                                : b.payment_detail_method === 'cheques_vacances' ? 'Chq.V.'
                                : b.payment_detail_method === 'credits' ? 'Crédits'
                                : b.payment_detail_method === 'lien' ? 'Lien'
                                : b.payment_detail_method === 'carte_cadeau' ? 'Carte cad.'
                                : b.payment_detail_method === 'sport_decouverte' ? 'Sport Déc.'
                                : b.payment_detail_method === 'adrenactive' ? 'Adrénactive'
                                : b.payment_detail_method === 'manawa' ? 'Manawa'
                                : b.payment_detail_method}
                            </span>
                          )}
                          {!b.payment_detail_method && (
                            <span className="text-[10px] text-slate-600 italic">
                              {b.payment_method === 'online' ? 'En ligne' : b.payment_method === 'credits' ? 'Crédits' : b.payment_method === 'gift_card' ? 'Carte cad.' : ''}
                            </span>
                          )}
                        </div>
                      ) : b.status === 'cancelled' ? (
                        <Badge className="bg-red-500/15 text-red-400 border-0 text-[10px] font-bold w-fit">Annulé</Badge>
                      ) : (
                        <Badge className="bg-red-500/15 text-red-400 border-0 text-[10px] font-bold w-fit" data-testid={`unpaid-badge-${b.id}`}>
                          Non réglé
                        </Badge>
                      )}
                    </td>
                    {/* Status */}
                    <td className="p-2 sm:p-3">
                      {b.status === 'confirmed' ? (
                        <Badge className="bg-[#00FF00]/15 text-[#00FF00] border-0 text-[10px]">OK</Badge>
                      ) : b.status === 'pending_onsite' ? (
                        <Badge className="bg-red-500/15 text-red-400 border-0 text-[10px] font-bold">A payer</Badge>
                      ) : b.status === 'pending' ? (
                        <Badge className="bg-yellow-500/15 text-yellow-400 border-0 text-[10px]">Attente</Badge>
                      ) : b.status === 'pending_payment_link' ? (
                        <Badge className="bg-purple-500/15 text-purple-400 border-0 text-[10px]">Lien</Badge>
                      ) : (
                        <Badge className="bg-red-500/15 text-red-400 border-0 text-[10px]">Annulé</Badge>
                      )}
                    </td>
                    {/* Contract + Participants */}
                    <td className="p-2 sm:p-3 hidden lg:table-cell" data-testid={`booking-contract-${b.id}`}>
                      <div className="flex flex-col gap-1">
                        {contractOk ? (
                          <button
                            onClick={() => {
                              const token = localStorage.getItem('rw_token');
                              const bid = b.group_id || b.id;
                              window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/bookings/${bid}/contract/pdf?token=${token}`, '_blank');
                            }}
                            className="flex items-center gap-1 text-[#00FF00] text-[10px] font-medium hover:underline cursor-pointer"
                            data-testid={`download-contract-${b.id}`}
                          >
                            <FileCheck className="w-3 h-3" /> Voir PDF
                          </button>
                        ) : (
                          <span className="flex items-center gap-1 text-orange-400 text-[10px]">
                            <XCircle className="w-3 h-3" /> Non signe
                          </span>
                        )}
                        {participantsOk ? (
                          <span className="flex items-center gap-1 text-[#00FF00] text-[10px]">
                            <Users className="w-3 h-3" /> Enregistres
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-orange-400 text-[10px]">
                            <Users className="w-3 h-3" /> En attente
                          </span>
                        )}
                      </div>
                    </td>
                    {/* Photos */}
                    <td className="p-2 sm:p-3 hidden lg:table-cell" data-testid={`booking-photos-${b.id}`}>
                      <div className="flex items-center gap-2">
                        {photoCount > 0 ? (
                          <Badge className="bg-cyan-500/15 text-cyan-400 border-0 text-[10px]">
                            <Camera className="w-3 h-3 mr-1" /> {photoCount}
                          </Badge>
                        ) : (
                          <span className="text-[10px] text-slate-600">-</span>
                        )}
                        <label
                          onClick={e => e.stopPropagation()}
                          className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium cursor-pointer transition-colors ${
                            uploadingFor === b.id
                              ? 'bg-[#2A2A2A] text-slate-500'
                              : 'bg-[#00FF00]/10 text-[#00FF00] hover:bg-[#00FF00]/20'
                          }`}
                          data-testid={`quick-upload-${b.id}`}
                        >
                          {uploadingFor === b.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            className="hidden"
                            disabled={uploadingFor === b.id}
                            onChange={e => handleQuickPhotoUpload(b.id, e)}
                          />
                        </label>
                      </div>
                    </td>
                    {/* Actions */}
                    <td className="p-2 sm:p-3 hidden sm:table-cell">
                      {b.status === 'pending_onsite' && (
                        <Button size="sm" onClick={(e) => { e.stopPropagation(); setSelectedBookingId(b.id); }}
                          className="bg-[#00FF00] text-black hover:bg-[#00CC00] text-[10px] font-bold h-7 px-2"
                          data-testid={`confirm-onsite-${b.id}`}>
                          <CheckCircle className="w-3 h-3 mr-1" /> Régler
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {displayedBookings.length === 0 && (
                <tr><td colSpan={10} className="p-8 text-center text-slate-600">{t('admin.no_bookings')}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* New Booking Dialog */}
      <Dialog open={showBookingForm} onOpenChange={setShowBookingForm}>
        <DialogContent className="sm:max-w-lg bg-[#1A1A1A] border-[#2A2A2A] max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-white">Nouvelle reservation</DialogTitle><DialogDescription className="text-slate-500">Creer une reservation</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2 mb-2">
              <Button variant={clientMode === 'existing' ? 'default' : 'outline'} size="sm" onClick={() => setClientMode('existing')} className={clientMode === 'existing' ? 'bg-[#00FF00] text-black' : 'text-slate-400 border-[#2A2A2A]'} data-testid="client-mode-existing">Client existant</Button>
              <Button variant={clientMode === 'new' ? 'default' : 'outline'} size="sm" onClick={() => setClientMode('new')} className={clientMode === 'new' ? 'bg-[#00FF00] text-black' : 'text-slate-400 border-[#2A2A2A]'} data-testid="client-mode-new">Nouveau client</Button>
            </div>
            {clientMode === 'existing' ? (
              <div>
                <Label className="text-sm font-semibold text-slate-300">Rechercher un client</Label>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-3 text-slate-500" />
                  <Input value={clientSearch} onChange={e => { setClientSearch(e.target.value); setBookingForm(p => ({ ...p, client_id: '' })); }} placeholder="Nom ou email..." className="bg-[#0A0A0A] border-[#2A2A2A] text-white pl-10" data-testid="client-search" />
                </div>
                {clientSearch && !bookingForm.client_id && (
                  <div className="max-h-32 overflow-y-auto bg-[#0A0A0A] border border-[#2A2A2A] rounded-lg mt-1">
                    {filteredClients.slice(0, 8).map(c => (
                      <button key={c.id} onClick={() => selectClientForBooking(c)} className="w-full text-left p-2 hover:bg-[#222] flex justify-between items-center" data-testid={`select-client-${c.id}`}>
                        <span className="text-white text-sm">{c.name}</span><span className="text-xs text-slate-500">{c.email}</span>
                      </button>
                    ))}
                    {filteredClients.length === 0 && <p className="p-2 text-xs text-slate-500">Aucun resultat</p>}
                  </div>
                )}
                {bookingForm.client_id && <Badge className="bg-[#00FF00]/15 text-[#00FF00] border-0 mt-2">{bookingForm.client_name} - {bookingForm.client_email}</Badge>}
              </div>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs text-slate-400">Nom *</Label><Input value={bookingForm.client_name} onChange={e => setBookingForm(p => ({ ...p, client_name: e.target.value }))} className="bg-[#0A0A0A] border-[#2A2A2A] text-white" data-testid="booking-client-name" /></div>
                  <div><Label className="text-xs text-slate-400">Email *</Label><Input value={bookingForm.client_email} onChange={e => setBookingForm(p => ({ ...p, client_email: e.target.value }))} className="bg-[#0A0A0A] border-[#2A2A2A] text-white" data-testid="booking-client-email" /></div>
                </div>
                <div><Label className="text-xs text-slate-400">Telephone</Label><Input value={bookingForm.client_phone} onChange={e => setBookingForm(p => ({ ...p, client_phone: e.target.value }))} className="bg-[#0A0A0A] border-[#2A2A2A] text-white" /></div>
              </div>
            )}
            {!isFranchise && <div><Label className="text-sm font-semibold text-slate-300">Site</Label><Select value={bookingForm.site_id} onValueChange={v => setBookingForm(p => ({ ...p, site_id: v, activity_id: '' }))}><SelectTrigger className="bg-[#0A0A0A] border-[#2A2A2A] text-white"><SelectValue /></SelectTrigger><SelectContent className="bg-[#1A1A1A] border-[#2A2A2A]">{sites.map(s => <SelectItem key={s.id} value={s.id}>{s.city}</SelectItem>)}</SelectContent></Select></div>}
            <div><Label className="text-sm font-semibold text-slate-300">Activite</Label><Select value={bookingForm.activity_id} onValueChange={v => { const act = activities.find(a => a.id === v); setBookingForm(p => ({ ...p, activity_id: v, duration_minutes: act?.durations[0]?.duration_minutes || 30 })); }}><SelectTrigger className="bg-[#0A0A0A] border-[#2A2A2A] text-white" data-testid="booking-activity"><SelectValue placeholder="Choisir..." /></SelectTrigger><SelectContent className="bg-[#1A1A1A] border-[#2A2A2A]">{filteredActivitiesForBooking.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent></Select></div>
            {selectedActivityForBooking && <div><Label className="text-sm font-semibold text-slate-300">Duree</Label><Select value={String(bookingForm.duration_minutes)} onValueChange={v => setBookingForm(p => ({ ...p, duration_minutes: Number(v) }))}><SelectTrigger className="bg-[#0A0A0A] border-[#2A2A2A] text-white"><SelectValue /></SelectTrigger><SelectContent className="bg-[#1A1A1A] border-[#2A2A2A]">{selectedActivityForBooking.durations.map(d => <SelectItem key={d.duration_minutes} value={String(d.duration_minutes)}>{d.label} - {d.price}EUR</SelectItem>)}</SelectContent></Select></div>}
            <div>
              <Label className="text-sm font-semibold text-slate-300">Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={`w-full justify-start text-left bg-[#0A0A0A] border-[#2A2A2A] hover:bg-[#111] ${bookingForm.date ? 'text-white' : 'text-slate-500'}`} data-testid="booking-date">
                    <CalendarDays className="w-4 h-4 mr-2 text-[#00FF00]" />
                    {bookingForm.date ? format(parseISO(bookingForm.date), 'dd MMMM yyyy', { locale: fr }) : 'Choisir une date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-[#1A1A1A] border-[#2A2A2A]" align="start">
                  <CalendarPicker mode="single" selected={bookingForm.date ? parseISO(bookingForm.date) : undefined} onSelect={d => { if (d) setBookingForm(p => ({ ...p, date: format(d, 'yyyy-MM-dd') })); }} locale={fr} className="text-white" />
                </PopoverContent>
              </Popover>
            </div>
            {bookingForm.date && bookingForm.activity_id && (
              <div>
                <Label className="text-sm font-semibold text-slate-300">Creneau disponible</Label>
                {bookingSlotsLoading ? <div className="flex items-center gap-2 text-slate-500 py-2"><Loader2 className="w-4 h-4 animate-spin" /> Chargement...</div> : bookingSlots.length > 0 ? (
                  <div className="grid grid-cols-4 gap-2 mt-2" data-testid="booking-slots-grid">
                    {bookingSlots.map(slot => {
                      const time = typeof slot === 'string' ? slot : slot.time;
                      const remaining = typeof slot === 'string' ? null : slot.remaining;
                      return (<button key={time} onClick={() => setBookingForm(p => ({ ...p, time_slot: time }))} className={`py-2 px-3 rounded-lg text-xs font-semibold text-center transition-all ${bookingForm.time_slot === time ? 'bg-[#00FF00] text-black' : 'bg-[#0A0A0A] border border-[#2A2A2A] text-slate-300 hover:border-[#00FF00]'}`} data-testid={`booking-slot-${time}`}>
                        {time}{remaining !== null && <span className={`block text-[10px] ${bookingForm.time_slot === time ? 'text-black/60' : remaining <= 2 ? 'text-orange-400' : 'text-slate-500'}`}>{remaining} place{remaining > 1 ? 's' : ''}</span>}
                      </button>);
                    })}
                  </div>
                ) : <p className="text-xs text-slate-500 mt-1">Aucun creneau disponible</p>}
              </div>
            )}
            <div><Label className="text-sm font-semibold text-slate-300">Statut</Label><Select value={bookingForm.payment_status} onValueChange={v => setBookingForm(p => ({ ...p, payment_status: v }))}><SelectTrigger className="bg-[#0A0A0A] border-[#2A2A2A] text-white"><SelectValue /></SelectTrigger><SelectContent className="bg-[#1A1A1A] border-[#2A2A2A]"><SelectItem value="confirmed">Deja paye</SelectItem><SelectItem value="pending_onsite">A payer sur place</SelectItem><SelectItem value="pay_by_link">Payer par lien (email)</SelectItem></SelectContent></Select></div>
            <div><Label className="text-sm font-semibold text-slate-300">Quantite (places)</Label><Input type="number" min={1} max={10} value={bookingForm.quantity} onChange={e => setBookingForm(p => ({ ...p, quantity: parseInt(e.target.value) || 1 }))} className="bg-[#0A0A0A] border-[#2A2A2A] text-white w-24" data-testid="booking-quantity" /></div>
            {selectedActivityForBooking && bookingForm.quantity > 0 && <div className="bg-[#0A0A0A] rounded-lg p-3 border border-[#2A2A2A]"><p className="text-sm text-slate-300">Total: <strong className="text-[#00FF00]">{(() => { const dur = selectedActivityForBooking.durations.find(d => d.duration_minutes === bookingForm.duration_minutes); return dur ? (dur.price * bookingForm.quantity).toFixed(2) : '0'; })()}EUR</strong>{bookingForm.quantity > 1 && <span className="text-slate-500 text-xs ml-2">({bookingForm.quantity} x {selectedActivityForBooking.durations.find(d => d.duration_minutes === bookingForm.duration_minutes)?.price || 0}EUR)</span>}</p></div>}
            <div><Label className="text-sm font-semibold text-slate-300">Notes</Label><Textarea value={bookingForm.notes} onChange={e => setBookingForm(p => ({ ...p, notes: e.target.value }))} rows={2} className="bg-[#0A0A0A] border-[#2A2A2A] text-white resize-none" /></div>
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setShowBookingForm(false)} className="text-slate-400">Annuler</Button><Button onClick={saveBooking} className="bg-[#00FF00] text-black hover:bg-[#00CC00] font-bold" data-testid="save-booking-btn">Creer</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <BookingDetailModal
        bookingId={selectedBookingId}
        open={!!selectedBookingId}
        onClose={() => { setSelectedBookingId(null); loadData(); }}
        onConfirmPayment={confirmOnsitePayment}
        API_BASE={API_BASE}
        activities={activities}
        onRefresh={loadData}
      />
    </>
  );
};
